import React from 'react';
import { Paper, Typography, Box, Skeleton } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface StatsWidgetProps {
  title: string;
  value: string | number;
  subValue?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  loading?: boolean;
  sx?: SxProps<Theme>;
}

const StatsWidget: React.FC<StatsWidgetProps> = ({
  title,
  value,
  subValue,
  trend,
  icon,
  color = 'primary',
  loading = false,
  sx,
}) => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {loading ? (
        <>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={48} sx={{ mt: 1 }} />
          <Skeleton variant="text" width="50%" height={20} sx={{ mt: 1 }} />
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {icon && (
              <Box sx={{ color: `${color}.main`, opacity: 0.8 }}>
                {icon}
              </Box>
            )}
          </Box>

          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 'bold',
              color: `${color}.main`,
              my: 1,
            }}
          >
            {value}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: trend.direction === 'up' ? 'success.main' : 'error.main',
                }}
              >
                {trend.direction === 'up' ? (
                  <TrendingUpIcon fontSize="small" />
                ) : (
                  <TrendingDownIcon fontSize="small" />
                )}
                <Typography variant="body2" component="span">
                  {trend.value}%
                </Typography>
              </Box>
            )}
            {subValue && (
              <Typography variant="body2" color="text.secondary">
                {subValue}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default StatsWidget;
